import { create } from 'zustand';

// Define the types for the user status and message limits
export type UserStatus = 'guest' | 'loggedin_basic' | 'loggedin_premium' | 'unknown';

export interface MessageLimits {
  current: number;
  max: number | null; // Max can be null for unlimited or if not yet defined
}

export interface ApiUrls {
  loginUrl?: string;
  upgradeUrl?: string;
  backendBaseUrl?: string;
  // Add other relevant URLs as needed
}

// Define the state structure for the auth store
interface AuthState {
  jwt: string | null;
  userStatus: UserStatus;
  messageLimits: MessageLimits | null;
  apiUrls: ApiUrls | null;
  isLoadingInitialData: boolean;
  initialDataError: string | null;
  isAuthenticated: boolean; // Derived from JWT presence, true if JWT exists
  isRateLimited: boolean; // New state for rate limiting
  apiError: string | null; // New state for general API errors (auth, rate limit, etc.)
}

// Define the actions for the auth store
interface AuthActions {
  setInitialData: (data: {
    jwt?: string | null;
    userStatus?: UserStatus;
    messageLimits?: MessageLimits | null;
    apiUrls?: ApiUrls | null;
  }) => void;
  setLoadingInitialData: (isLoading: boolean) => void;
  setInitialDataError: (error: string | null) => void;
  clearAuthData: () => void; // For logout
  setJwt: (jwt: string | null) => void; // Potentially for token refresh scenarios
  setAuthError: (errorMessage: string) => void; // Action for auth errors (401/403)
  setRateLimitError: (isLimited: boolean, errorMessage?: string) => void; // Action for rate limit errors (429)
  clearApiError: () => void; // Action to clear API errors and rate limit status
}

// Define the initial state
const initialState: AuthState = {
  jwt: null,
  userStatus: 'unknown',
  messageLimits: null,
  apiUrls: null,
  isLoadingInitialData: false,
  initialDataError: null,
  isAuthenticated: false,
  isRateLimited: false, // Initialize new state
  apiError: null, // Initialize new state
};

// Create the Zustand store
const useAuthStore = create<AuthState & AuthActions>((set) => ({
  ...initialState,

  setInitialData: (data) =>
    set((state) => ({
      ...state,
      jwt: data.jwt !== undefined ? data.jwt : state.jwt,
      userStatus: data.userStatus !== undefined ? data.userStatus : state.userStatus,
      messageLimits: data.messageLimits !== undefined ? data.messageLimits : state.messageLimits,
      apiUrls: data.apiUrls !== undefined ? data.apiUrls : state.apiUrls,
      isAuthenticated: data.jwt !== undefined ? !!data.jwt : !!state.jwt, // Update isAuthenticated based on JWT
      isLoadingInitialData: false,
      initialDataError: null,
      apiError: null, // Clear previous API errors on new initial data
      isRateLimited: false, // Reset rate limit status on new initial data
    })),

  setLoadingInitialData: (isLoading) =>
    set({ isLoadingInitialData: isLoading, initialDataError: null }),

  setInitialDataError: (error) =>
    set({ initialDataError: error, isLoadingInitialData: false }),

  clearAuthData: () =>
    set({
      ...initialState, // Reset to initial state
      userStatus: 'guest', // Explicitly set to guest on logout/clear
      isAuthenticated: false,
      apiError: null,
      isRateLimited: false,
    }),

  setJwt: (jwt) =>
    set((state) => ({
        jwt,
        isAuthenticated: !!jwt,
        apiError: null, // Clear API error if JWT is successfully set/cleared
        isRateLimited: !jwt ? state.isRateLimited : false, // Reset rate limit if JWT is removed, otherwise keep or reset
    })),

  setAuthError: (errorMessage) =>
    set({
      jwt: null,
      isAuthenticated: false,
      userStatus: 'guest', // Or 'unknown', depending on desired behavior post-auth-error
      apiError: errorMessage,
      isRateLimited: false, // Typically auth error means further action isn't rate limited yet
      // messageLimits: null, // Optionally clear message limits
      // apiUrls: null, // Optionally clear apiUrls
    }),

  setRateLimitError: (isLimited, errorMessage) =>
    set({
      isRateLimited: isLimited,
      apiError: errorMessage || (isLimited ? 'Limită de mesaje depășită' : null),
    }),

  clearApiError: () =>
    set({
      apiError: null,
      isRateLimited: false,
    }),
}));

export default useAuthStore; 